import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { message } from "antd";
import { APIResponse } from "../types";
import { navigateToLogin } from "../utils/navigation";

// Create axios instance with base configuration
const httpClient: AxiosInstance = axios.create({
  baseURL: "/",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add authentication token if available
    const token = localStorage.getItem("Cookie");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add trace ID for request tracking
    const traceId = generateTraceId();
    if (config.headers) {
      config.headers["X-Trace-ID"] = traceId;
    }

    // Log request for debugging
    console.log(
      `[HTTP Request] ${config.method?.toUpperCase()} ${config.url}`,
      {
        traceId,
        data: config.data,
        params: config.params,
      }
    );

    return config;
  },
  (error: AxiosError) => {
    console.error("[HTTP Request Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor
httpClient.interceptors.response.use(
  (response: AxiosResponse<APIResponse>) => {
    const { data } = response;
    if (response.status === 200) {
      if (!data.success) {
        // 登录态过期 需要跳转到登录界面
        if (
          data &&
          (data.errorCode === "SC-A4010" || data.errorCode === 401000)
        ) {
          navigateToLogin();
        }
        const message =
          data.message || data.errorMsg || data.errMsg || data.errorMessage;
        const error = new Error(message || JSON.stringify(data));
        error.code = data.errorCode;
        error.data = data;
        error.originRes = response;
        error.success = false;
        return Promise.reject(error);
      }
    }

    const traceId =
      response.headers["x-trace-id"] || response.config.headers?.["X-Trace-ID"];

    // Log response for debugging
    console.log(
      `[HTTP Response] ${response.config.method?.toUpperCase()} ${
        response.config.url
      }`,
      {
        traceId,
        success: data.success,
        data: data.data,
      }
    );

    return response;
  },
  (error: AxiosError<APIResponse>) => {
    const traceId =
      error.response?.headers?.["x-trace-id"] ||
      error.config?.headers?.["X-Trace-ID"];

    console.error("[HTTP Response Error]", {
      traceId,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });

    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 401:
          message.error("登录已过期，请重新登录");
          // Redirect to login page
          navigateToLogin();
          break;
        case 403:
          message.error("您没有权限访问此功能，请联系管理员");
          break;
        case 404:
          message.error("请求的资源不存在");
          break;
        case 500:
          message.error("系统繁忙，请稍后再试");
          break;
        default:
          if (data?.errorMessage) {
            message.error(data.errorMessage);
          } else {
            message.error(`请求失败 (${status})`);
          }
      }

      // Enhance error with API response data
      if (data) {
        (error as any).traceId = data.traceId || traceId;
        (error as any).errorCode = data.errorCode;
        (error as any).apiError = true;
      }
    } else if (error.request) {
      // Network error
      console.error("[Network Error]", error.request);
      // message.error("网络连接异常，请检查网络后重试");
      (error as any).networkError = true;
    } else {
      // Other errors
      console.error("[Request Setup Error]", error.message);
      message.error("请求配置错误");
    }

    return Promise.reject(error);
  }
);

// Utility function to generate trace ID
function generateTraceId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Utility function to handle API errors
export const handleAPIError = (error: any) => {
  if (error.apiError) {
    console.error(`API Error [${error.traceId}]:`, error.message);
    return {
      success: false,
      errorMessage: error.message,
      errorCode: error.errorCode,
      traceId: error.traceId,
    };
  } else if (error.networkError) {
    console.error("Network Error:", error.message);
    return {
      success: false,
      errorMessage: "网络连接异常，请检查网络后重试",
      errorCode: "NETWORK_ERROR",
    };
  } else {
    console.error("Unknown Error:", error.message);
    return {
      success: false,
      errorMessage: "未知错误，请稍后重试",
      errorCode: "UNKNOWN_ERROR",
    };
  }
};

export default httpClient;
